<script lang="ts">
    
    import { getContext, onDestroy } from "svelte";
    import type { Fn, GameRankingDto, RealTimeEvent } from "../../global";
    import type { Game } from "../../Model/Game";   
    import type { State } from "../../Model/State";
    import { eventsChannelFactory, room, playerService, roomsService } from "../../Services";
    import type { EventsChannel } from "../../Services/EventsChannel";
    import type { Player } from "../../Model/Player";
    import PictureControl from "../Shared/PictureControl.svelte";
    import Fa from "svelte-fa";
    import { faFloppyDisk, faPencil } from "@fortawesome/free-solid-svg-icons";
    import ControlFeedback from "../Shared/ControlFeedback.svelte";
    import InvalidFeedback from "../Shared/InvalidFeedback.svelte";
    import { i18n } from "../../Services/i18n";

    export let player: Player;
    
    const game: State<Game | undefined> = getContext('game');
    
    let gameStateUnsubscriber: Fn;
    let gameRanking: GameRankingDto;
    let roomEvents: EventsChannel;
    let editing: boolean = false;
    let correct = true;

    $: setupComponent($game);

    onDestroy(() => {
        gameStateUnsubscriber?.();
        roomEvents?.destroy();
    });

    function setupComponent(game: Game | undefined): void {
        gameStateUnsubscriber?.();
        roomEvents?.destroy();

        if (!game) {
            gameRanking = undefined;
            return;
        }
        
        gameStateUnsubscriber = game.state.subscribe(gameState => {
            if (gameState !== 'playing') {
                return;
            }
            getRanking(game.id);
        });

        roomEvents = eventsChannelFactory.createEventsChannel(`room.${game.roomId}`);
        roomEvents.on('ranking', onRankingUpdate);
    }

    async function getRanking(gameId: string): Promise<void> {
        try {
            gameRanking = await roomsService.getRanking(gameId);
        } catch (error) {
            console.error(error);
        }
    }

    function onRankingUpdate({ data }: RealTimeEvent<GameRankingDto>): void {
        gameRanking = data;
    }

    function editName() {
        editing = !editing;
    }

    function checkText() {
        const pattern = new RegExp(/^[a-zA-Z]+$/g);
        correct = pattern.test(player.name.value);
    }

    async function savePlayer() {
        await playerService.register(player.name.value, player.pictureId.value, $game.roomId, $game.id);
        editName();
    }

</script>

{#if player}
    <div class="player-form">
        <div class="form-control f-center">
            <PictureControl bind:pictureId={player.pictureId} playerName={player.name.value} identityOverride={player.identityOverride.value}/>
        </div>
        {#if !$room.offline.value}
            <div class="player-name">
                {#if !editing}
                    {player.name.value}
                    {#if !player.identityOverride.value}
                        <button class='edit-name' on:click={editName}>
                            <Fa icon={faPencil}/>
                        </button>
                    {/if}
                {:else}
                    <div style="display: flex; gap: 5px;">
                        <div class="form-control">
                            <ControlFeedback let:input let:invalid>
                                <input
                                    type="text"
                                    required
                                    maxlength="20"
                                    bind:value={player.name.value}
                                    on:input={checkText}
                                    placeholder={$i18n("Nombre de jugador")}
                                    class="name-input"
                                />
                                <InvalidFeedback show={input && invalid}>
                                    {$i18n("Debe introducir un nombre de usuario")}
                                </InvalidFeedback>
                                <InvalidFeedback show={!correct && !invalid}>
                                    {$i18n("No debe contener números")}
                                </InvalidFeedback>
                            </ControlFeedback>
                        </div>
                        <button class='save-name' on:click={savePlayer}>
                            <Fa icon={faFloppyDisk}/>
                        </button>
                    </div>

                {/if}
            </div>
        {:else}
            <div class="player-name">
                {player.name.value}
            </div>
        {/if}
    </div>
{/if}

<!-- {#if gameRanking}
    <div class="game-ranking">
        {#each gameRanking.players as player}
            <div class="ranking-position">
                <div class="picture">
                    <img src={pictureUrlFactory.getPictureUrl(player.pictureId)} alt="">
                </div>
                <div class="player-name">{player.name}</div>
                <div class="position">{player.numChecked}/{gameRanking.numCells}</div>
            </div>
        {/each}
    </div>
{/if} -->

<style lang="scss">
    .player-form {
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    .game-ranking {
        padding: 0.5rem;
    }

    .ranking-position {
        display: flex;
        align-items: center;
        gap: 0.5rem;

        & + .ranking-position {
            margin-top: 0.5rem;
        }
    }

    .picture {
        width: 3rem;
        height: 3rem;
        border-radius: 50%;
        overflow: hidden;

        img {
            display: block;
            width: 100%;
            height: 100%;
            object-fit: cover;
        }
    }

    .player-name {
        flex: 1 1 0;
    }

    .edit-name {
        position: relative;
        width: 30px;
        height: 30px;
        font-size: 1rem;
        padding: 5px;
    }

    .save-name {
        position: relative;
        width: 35px;
        height: 35px;
        font-size: 1rem;
        padding: 5px;
    }

</style>
